import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { listSubmissions, releaseExplanations, listMembers, type Submission, type Member } from "../../services/companies-api";

const STATUS_LABEL: Record<string, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  submitted: "Submitted",
  pending_grading: "Pending grading",
  completed: "Completed",
};

const STATUS_OPTIONS: Submission["status"][] = [
  "not_started",
  "in_progress",
  "submitted",
  "pending_grading",
  "completed",
];

/** FR-031: the CO Member's full submissions overview — every status, not just `pending_grading`
 * (the grading queue, T078, only ever shows that one status). This is also the only planned UI
 * path that can release explanations for a submission with no free-text questions at all, since
 * such a submission never appears in the grading queue. */
export function SubmissionsPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [statusFilter, setStatusFilter] = useState<Submission["status"] | "">("");
  const [releasing, setReleasing] = useState<string | null>(null);

  async function refresh() {
    if (!companyId) return;
    setSubmissions(await listSubmissions(companyId, statusFilter || undefined));
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, statusFilter]);

  useEffect(() => {
    if (!companyId) return;
    listMembers(companyId).then(setMembers);
  }, [companyId]);

  function memberName(memberId: string) {
    return members.find((m) => m.id === memberId)?.fullName ?? memberId;
  }

  async function handleRelease(submissionId: string) {
    if (!companyId) return;
    setReleasing(submissionId);
    try {
      await releaseExplanations(companyId, submissionId);
      await refresh();
    } finally {
      setReleasing(null);
    }
  }

  return (
    <div className="p-6">
      <button onClick={() => navigate(`/co-member/companies/${companyId}`)} className="mb-4 text-sm text-brand">
        ← Back to company
      </button>
      <h1 className="mb-4 font-sans text-xl text-ink">Submissions</h1>

      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value as Submission["status"] | "")}
        className="mb-4 rounded border border-surface-alt px-3 py-2 text-sm"
      >
        <option value="">All statuses</option>
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s}>
            {STATUS_LABEL[s]}
          </option>
        ))}
      </select>

      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-surface-alt text-body">
            <th className="py-2">Member</th>
            <th>Status</th>
            <th>Score</th>
            <th>Explanations</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((s) => (
            <tr key={s.id} className="border-b border-surface-alt">
              <td className="py-2 text-ink">{memberName(s.memberId)}</td>
              <td>{STATUS_LABEL[s.status]}</td>
              <td>{s.scorePercent !== null ? `${s.scorePercent}%` : "—"}</td>
              <td>{s.explanationsReleasedAt !== null ? "Released" : "Withheld"}</td>
              <td>
                {s.status === "completed" && s.explanationsReleasedAt === null && (
                  <button
                    onClick={() => handleRelease(s.id)}
                    disabled={releasing === s.id}
                    className="text-sm text-brand"
                  >
                    Release explanations
                  </button>
                )}
              </td>
            </tr>
          ))}
          {submissions.length === 0 && (
            <tr>
              <td colSpan={5} className="py-6 text-center text-body">
                No submissions match this filter.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
